import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Download, ExternalLink } from "lucide-react";
import RoboticsShell from "./RoboticsShell";

/**
 * /robotics/qualifications — public marketing page introducing the four
 * external qualifications FutureTec content aligns with.
 *
 * All alignment percentages are read live from `/api/robotics/qualifications`
 * so a re-run of `build_alignment.py` immediately updates this page — the
 * marketing copy never falls out of sync with the JSON.
 */
const QUAL_CARDS = [
  {
    slug: "btec_l12_first_award_engineering",
    body: "Pearson (BTEC)",
    name: "Level 1 / 2 First Award in Engineering",
    ageBand: "14–16 (typically studied in KS4)",
    coverage_key: "btec_l12_first_award_engineering_full_or_strong",
    strongest: "Builder + Engineer bands",
    pdf: "futuretec_btec_alignment.pdf",
    learn_more: "https://qualifications.pearson.com/en/qualifications/btec-firsts/engineering-2012-nqf.html",
    accent: "gold",
    priority: true,
  },
  {
    slug: "crest_awards",
    body: "British Science Association",
    name: "CREST Awards — Bronze · Silver · Gold",
    ageBand: "11+ (Bronze) · 14+ (Silver) · 16+ (Gold)",
    coverage_key: "crest_awards_full_or_strong",
    coverage_note: "capstone-anchored — every band's capstone project targets one CREST level",
    strongest: "capstone projects across all three bands",
    pdf: "futuretec_crest_alignment.pdf",
    learn_more: "https://www.crestawards.org/",
    accent: "phosphor",
  },
  {
    slug: "aqa_gcse_dt_electronic_systems",
    body: "AQA",
    name: "GCSE Design & Technology (8552) — Electronic Systems",
    ageBand: "14–16 (KS4)",
    coverage_key: "aqa_gcse_dt_electronic_systems_full_or_strong",
    strongest: "Builder band (microcontrollers + programmable systems)",
    pdf: "futuretec_aqa_gcse_dt_alignment.pdf",
    learn_more: "https://www.aqa.org.uk/subjects/design-and-technology/gcse/design-and-technology-8552",
    accent: "copper",
  },
  {
    slug: "arduino_fundamentals_cert",
    body: "Arduino LLC",
    name: "Arduino Fundamentals Certification",
    ageBand: "16+ recommended",
    coverage_key: "arduino_fundamentals_cert_full_or_strong",
    strongest: "Builder band — direct hit for 6 of the 8 exam topics",
    pdf: "futuretec_arduino_cert_alignment.pdf",
    learn_more: "https://www.arduino.cc/education/certification",
    accent: "copper",
  },
];

export default function RoboticsQualifications() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    fetch("/api/robotics/qualifications/summary", { cache: "no-store" })
      .then((r) => r.ok ? r.json() : null)
      .then(setSummary)
      .catch(() => {});
  }, []);
  const pct = (key) => summary?.coverage_percentages?.[key];

  return (
    <RoboticsShell soft>
      <main className="rbx-page rbx-qual" data-testid="rbx-qualifications-page">
        {/* Hero */}
        <header className="rbx-qual__hero">
          <div className="rbx-qual__eyebrow" data-testid="rbx-qual-eyebrow">FTG · ROBOTICS ACADEMY · QUALIFICATIONS</div>
          <h1 className="rbx-qual__title" data-testid="rbx-qual-title">Recognised pathways.<br/>Real futures.</h1>
          <p className="rbx-qual__lede" data-testid="rbx-qual-lede">
            FutureTec Robotics Academy content aligns with the world&rsquo;s
            leading engineering and STEM qualifications, giving your child
            two credentials from one journey &mdash; a FutureTec certificate
            <em> and</em> a direct pathway into external recognition that
            top universities and employers already understand.
          </p>
        </header>

        {/* Qualification cards */}
        <section className="rbx-qual__grid" data-testid="rbx-qual-grid">
          {QUAL_CARDS.map((q) => (
            <article key={q.slug} className={`rbx-qual__card rbx-qual__card--${q.accent} ${q.priority ? "is-priority" : ""}`} data-testid={`rbx-qual-card-${q.slug}`}>
              {q.priority && <div className="rbx-qual__ribbon">PRIORITY PATHWAY</div>}
              <div className="rbx-qual__card-body">
                <div className="rbx-qual__card-mono">{q.body}</div>
                <h3 className="rbx-qual__card-name" data-testid={`rbx-qual-name-${q.slug}`}>{q.name}</h3>
                <dl className="rbx-qual__meta">
                  <dt>Age range</dt><dd>{q.ageBand}</dd>
                  <dt>Strongest fit</dt><dd>{q.strongest}</dd>
                  <dt>FutureTec coverage</dt>
                  <dd data-testid={`rbx-qual-coverage-${q.slug}`}>
                    {pct(q.coverage_key) != null
                      ? <><strong>~{pct(q.coverage_key)}%</strong> of the 90-lesson curriculum</>
                      : (q.coverage_note || "capstone-anchored across all bands")}
                  </dd>
                </dl>
                <div className="rbx-qual__actions">
                  <a
                    href={`/api/downloads/${q.pdf}`}
                    className="rbx-btn rbx-btn--primary rbx-btn--sm"
                    data-testid={`rbx-qual-download-${q.slug}`}
                  >
                    <Download size={14}/> Alignment PDF
                  </a>
                  <a
                    href={q.learn_more}
                    target="_blank" rel="noopener noreferrer"
                    className="rbx-btn rbx-btn--ghost rbx-btn--sm"
                    data-testid={`rbx-qual-learn-${q.slug}`}
                  >
                    Learn more <ExternalLink size={12}/>
                  </a>
                </div>
              </div>
            </article>
          ))}
        </section>

        {/* Pathway diagram */}
        <section className="rbx-qual__btec-plan" data-testid="rbx-qual-btec-plan">
          <div className="rbx-qual__btec-plan-head">
            <h2>How our BTEC certification pathway will work</h2>
            <span className="rbx-qual__status-badge" data-testid="rbx-qual-btec-status">
              In progress &mdash; Q3 2026 target
            </span>
          </div>
          <p className="rbx-qual__btec-lede">
            <em>We believe in being transparent about where we are on the
            qualification journey. Here is our honest roadmap:</em>
          </p>

          <div className="rbx-qual__btec-grid">
            <div className="rbx-qual__btec-block is-now">
              <h3>Available today</h3>
              <ul>
                <li>90 first-principles engineering lessons across three age bands (Explorer, Builder, Engineer)</li>
                <li>Curriculum content mapped and aligned with BTEC (Pearson) Level 1/2 Engineering learning outcomes</li>
                <li>FutureTec Robotics Academy internal certificates upon band completion</li>
                <li>Downloadable BTEC alignment report available above</li>
              </ul>
            </div>

            <div className="rbx-qual__btec-block is-next">
              <h3>Coming Q3 2026 <span className="rbx-qual__btec-block-sub">(pending Pearson approval)</span></h3>
              <ul>
                <li>Formal BTEC certification through <strong>kit-shipped online delivery</strong></li>
                <li>Real hardware components shipped to each enrolled student &mdash; no simulator-only shortcuts</li>
                <li>Video-evidenced practical assessments meeting Pearson&rsquo;s online-delivery standards</li>
                <li>Qualified assessors and Internal Verification (IV) processes in place</li>
                <li>Regular External Verification (EV) audits by Pearson</li>
              </ul>
            </div>
          </div>

          <div className="rbx-qual__btec-narrative">
            <h3>How the kit-shipped model works</h3>
            <p>
              Students receive a physical component kit at the start of
              each band. They complete real hands-on builds &mdash; soldering,
              wiring, coding &mdash; and submit video evidence of their work.
              Our qualified assessors review each submission against BTEC
              unit criteria. This is not distance-learning-lite. It is
              engineering learned properly, verified rigorously, and
              certified formally.
            </p>

            <h3>Why we&rsquo;re doing it this way</h3>
            <p>
              FutureTec is anchored in the belief that engineers are made
              through real hands, not simulations alone. By combining
              rigorous online delivery with shipped hardware kits, we can
              offer a Pearson-recognised BTEC pathway to families anywhere
              in the Asia-Pacific &mdash; without asking students to travel
              to a physical centre. One qualification. Global delivery.
              Real hardware.
            </p>

            <h3>Current status</h3>
            <p>
              <strong>Qualification Evaluation UK Ltd.</strong> is preparing
              its application for Pearson Approved Centre status. We will
              update this page as our approval milestones are reached.
            </p>
          </div>

          <div className="rbx-qual__btec-cta">
            <Link
              to="/robotics/for-parents#newsletter"
              className="rbx-btn rbx-btn--primary"
              data-testid="rbx-qual-btec-register"
            >
              Register interest for BTEC cohort &rarr;
            </Link>
          </div>
        </section>

        {/* Pathway diagram */}
        <section className="rbx-qual__pathway" data-testid="rbx-qual-pathway">
          <h2>How the pathway works</h2>
          <div className="rbx-qual__pathway-flow">
            <div className="rbx-qual__step">
              <div className="rbx-qual__step-tag">EXPLORER</div>
              <div className="rbx-qual__step-name">Ages 8&ndash;11</div>
              <div className="rbx-qual__step-sub">Analog electronics from first principles</div>
              <div className="rbx-qual__step-target">→ ready for <strong>CREST Bronze</strong></div>
            </div>
            <div className="rbx-qual__arrow"><Award size={22}/></div>
            <div className="rbx-qual__step">
              <div className="rbx-qual__step-tag">BUILDER</div>
              <div className="rbx-qual__step-name">Ages 12&ndash;14</div>
              <div className="rbx-qual__step-sub">Arduino, code, state machines</div>
              <div className="rbx-qual__step-target">→ <strong>CREST Silver</strong> · <strong>AQA GCSE D&amp;T foundation</strong> · <strong>Arduino Cert</strong></div>
            </div>
            <div className="rbx-qual__arrow"><Award size={22}/></div>
            <div className="rbx-qual__step is-final">
              <div className="rbx-qual__step-tag">ENGINEER</div>
              <div className="rbx-qual__step-name">Ages 15&ndash;17</div>
              <div className="rbx-qual__step-sub">Sensor fusion, PID, edge AI, IoT</div>
              <div className="rbx-qual__step-target">→ <strong>CREST Gold</strong> · <strong>BTEC Level 2</strong> · pathway to HND &amp; degree top-up</div>
            </div>
          </div>
        </section>

        {/* Regional context */}
        <aside className="rbx-qual__region" data-testid="rbx-qual-region">
          <h3>A UK qualification recognised across the Asia-Pacific</h3>
          <p>
            FutureTec&rsquo;s alignment with UK awarding bodies opens doors
            across the Asia-Pacific region, where BTEC and CREST are
            established pathways to degrees at British, Australian,
            Singaporean and Malaysian universities.
          </p>
          <p style={{ marginTop: 10 }}>
            Our primary markets include:
          </p>
          <ul className="rbx-qual__region-list">
            <li>
              <strong>Sri Lanka, Malaysia, Hong Kong</strong> &mdash; where
              BTEC HND &rarr; UK degree top-up is a well-trodden and highly
              valued route.
            </li>
            <li>
              <strong>Singapore, Australia</strong> &mdash; where UK
              qualifications complement strong local vocational frameworks.
            </li>
            <li>
              <strong>Thailand, Pakistan, Bangladesh, India</strong> &mdash;
              growing markets where a UK-issued credential adds significant
              differentiation.
            </li>
          </ul>
          <p style={{ marginTop: 10 }}>
            By anchoring our qualification pathway in the UK through
            <strong> Qualification Evaluation UK Ltd.</strong>, we give
            students in every one of these markets access to internationally
            portable credentials &mdash; from a single trusted source.
          </p>
        </aside>

        {/* Trust footer */}
        <footer className="rbx-qual__trust" data-testid="rbx-qual-trust">
          FutureTec Robotics Academy is a trading name of
          <strong> Qualification Evaluation UK Ltd.</strong>, a United Kingdom
          company positioning to seek formal Approved Centre status with
          Pearson (BTEC) and recognition from the British Science Association
          (CREST). Positioned for global delivery across the Asia-Pacific
          under UK awarding body frameworks. All alignment claims are based
          on the current published specifications of each awarding body;
          each PDF cites its source URL and retrieval date.
        </footer>
      </main>
    </RoboticsShell>
  );
}
